// A small Markdown renderer for the guides of the illustrative projects (examples/skfitter/<id>/GUIDE.md):
// headings (#, ##, ###), paragraphs, bullet (* / -) and numbered lists, tables (| a | b |), block quotes (>), and
// inline **bold**, *italic*, `code` and [links](url). Everything is rendered as React elements (no raw HTML).
import { Anchor, Blockquote, Code, List, Table, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";

type Block =
  | { kind: "h"; level: number; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul" | "ol"; items: string[] }
  | { kind: "table"; head: string[]; rows: string[][] }
  | { kind: "quote"; text: string };

const cells = (line: string) => line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

export function parseMarkdown(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) { out.push({ kind: "h", level: h[1].length, text: h[2].trim() }); i++; continue; }
    if (/^\s*\|/.test(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && /^\s*\|/.test(lines[i])) rows.push(cells(lines[i++]));
      out.push({ kind: "table", head, rows });
      continue;
    }
    const bullet = /^\s*[*-]\s+/;
    const number = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || number.test(line)) {
      const re = bullet.test(line) ? bullet : number;
      const items: string[] = [];
      while (i < lines.length && lines[i].trim()) {
        if (re.test(lines[i])) items.push(lines[i].replace(re, ""));
        else if (/^\s+/.test(lines[i]) && items.length) items[items.length - 1] += " " + lines[i].trim();
        else break;
        i++;
      }
      out.push({ kind: re === bullet ? "ul" : "ol", items });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const parts: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) parts.push(lines[i++].replace(/^\s*>\s?/, ""));
      out.push({ kind: "quote", text: parts.join(" ") });
      continue;
    }
    const parts: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4})\s/.test(lines[i]) && !/^\s*\|/.test(lines[i])
      && !bullet.test(lines[i]) && !number.test(lines[i]) && !/^\s*>/.test(lines[i])) parts.push(lines[i++].trim());
    out.push({ kind: "p", text: parts.join(" ") });
  }
  return out;
}

/** Inline markup: `code`, **bold**, *italic*, [text](url). */
export function inline(text: string, key = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const k = `${key}-${n++}`;
    if (m[1]) out.push(<Code key={k}>{t.slice(1, -1)}</Code>);
    else if (m[2]) out.push(<strong key={k}>{inline(t.slice(2, -2), k)}</strong>);
    else if (m[3]) out.push(<em key={k}>{inline(t.slice(1, -1), k)}</em>);
    else {
      const [, label, href] = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(t)!;
      const safe = /^(https?:|mailto:|\/)/.test(href) ? href : undefined;
      out.push(safe ? <Anchor key={k} href={safe} target={safe.startsWith("/") ? undefined : "_blank"}
        rel="noreferrer">{inline(label, k)}</Anchor> : label);
    }
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ source }: { source: string }) {
  return (
    <div className="prayog-markdown">
      {parseMarkdown(source).map((b, i) => {
        const k = `b${i}`;
        switch (b.kind) {
          case "h":
            return <Title key={k} order={Math.min(6, b.level + 1) as 2 | 3 | 4 | 5} mt={i ? "md" : 0} mb={4}>{inline(b.text, k)}</Title>;
          case "p":
            return <Text key={k} size="sm" mb="xs">{inline(b.text, k)}</Text>;
          case "ul":
          case "ol":
            return (
              <List key={k} type={b.kind === "ol" ? "ordered" : "unordered"} size="sm" spacing={2} mb="xs" withPadding>
                {b.items.map((it, j) => <List.Item key={j}>{inline(it, `${k}-${j}`)}</List.Item>)}
              </List>
            );
          case "table":
            return (
              <Table.ScrollContainer key={k} minWidth={400} mb="sm">
                <Table striped withTableBorder fz="sm" verticalSpacing={4}>
                  <Table.Thead><Table.Tr>{b.head.map((c, j) => <Table.Th key={j}>{inline(c, `${k}-h${j}`)}</Table.Th>)}</Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {b.rows.map((r, j) => (
                      <Table.Tr key={j}>{r.map((c, l) => <Table.Td key={l}>{inline(c, `${k}-${j}-${l}`)}</Table.Td>)}</Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            );
          case "quote":
            return <Blockquote key={k} p="sm" mb="xs" fz="sm">{inline(b.text, k)}</Blockquote>;
        }
      })}
    </div>
  );
}
